import React, { useState } from "react";
import {
  useStripe, useElements,
  CardNumberElement, CardExpiryElement, CardCvcElement,
} from "@stripe/react-stripe-js";
import { Icon } from "@/components/Icon";
import BrandMark from "./BrandMark";

/*
  PRYMEBET-styled Stripe Elements card fields (CK-02 of PNRCheckoutSheet.dc.html).

  We render the card as three INDIVIDUAL Stripe Elements (CardNumber / CardExpiry /
  CardCvc) to match the design's separate fields, plus a plain ZIP <input>. Each
  Stripe element sits inside our own #111114 field wrapper with the inset #27272A
  border; the element's own text is styled (16px Outfit, white, tabular-nums) via
  the Elements `style` prop so it reads like the mockup's typed value.

  Confirm with stripe.confirmCardPayment(clientSecret, { payment_method: { card:
  cardNumberElement, billing_details: { address: { postal_code } } } }).
*/

// Stripe Element text style — matches the design's field typography.
const ELEMENT_STYLE = {
  base: {
    color: "#ffffff",
    fontFamily: "Outfit, sans-serif",
    fontWeight: "600",
    fontSize: "16px",
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "0.02em",
    "::placeholder": { color: "#52525b" },
    iconColor: "#a1a1aa",
  },
  invalid: { color: "#EF4444", iconColor: "#EF4444" },
  complete: { color: "#ffffff" },
};

// Field wrapper — the #111114 box with inset border, focus turns green.
function FieldBox({ children, focused, valid = true, style }) {
  const border = valid
    ? (focused ? "inset 0 0 0 1.5px #00D632" : "inset 0 0 0 1px #27272A")
    : "inset 0 0 0 1.5px #EF4444";
  return (
    <div style={{
      height: 52, boxSizing: "border-box", borderRadius: 12, background: "#111114",
      boxShadow: border, display: "flex", alignItems: "center", gap: 10,
      padding: "0 14px", ...style,
    }}>
      {children}
    </div>
  );
}

const labelStyle = (on = false) => ({ font: "600 12px 'Outfit'", color: on ? "#fff" : "#a1a1aa" });

export default function CardForm({ amountUsd, clientSecret, paymentIntentId, onPaid, onError }) {
  const stripe = useStripe();
  const elements = useElements();

  const [focus, setFocus] = useState(""); // which field is focused
  const [brand, setBrand] = useState(null); // detected card brand
  const [zip, setZip] = useState("");
  const [numberErr, setNumberErr] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Stripe reports brand as "visa" | "mastercard" | "amex" | "unknown" | …
  const brandKey = brand && ["visa", "mastercard", "amex"].includes(brand) ? brand : null;

  const pay = async () => {
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    try {
      const cardNumber = elements.getElement(CardNumberElement);
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardNumber,
          billing_details: { address: { postal_code: zip || undefined } },
        },
      });
      if (error) {
        // On a card decline Stripe attaches the PaymentMethod (brand + last4) to the error.
        const c = error.payment_method?.card;
        const info = c ? { brand: c.brand, last4: c.last4 } : (brand ? { brand } : null);
        onError?.(error.message || "Your card was declined. You were not charged.", info);
        return;
      }
      if (paymentIntent && paymentIntent.status === "succeeded") {
        onPaid?.(paymentIntent.id || paymentIntentId);
      } else {
        onError?.("Payment could not be completed. You were not charged.");
      }
    } catch (e) {
      onError?.(e?.message || "Something went wrong processing your card.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Card number */}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <span style={labelStyle()}>Card number</span>
        <FieldBox focused={focus === "number"} valid={!numberErr} style={{ padding: "0 12px 0 14px" }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <CardNumberElement
              options={{ style: ELEMENT_STYLE, placeholder: "1234 1234 1234 1234", showIcon: false }}
              onFocus={() => setFocus("number")}
              onBlur={() => setFocus("")}
              onChange={(e) => { setBrand(e.brand && e.brand !== "unknown" ? e.brand : null); setNumberErr(!!e.error); }}
            />
          </div>
          {brandKey && <BrandMark brand={brandKey} alt={brandKey} height={22} />}
        </FieldBox>
      </div>

      {/* Expiry / CVC / ZIP */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,minmax(0,1fr))", gap: 10 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          <span style={labelStyle()}>Expiry</span>
          <FieldBox focused={focus === "expiry"}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <CardExpiryElement
                options={{ style: ELEMENT_STYLE, placeholder: "MM / YY" }}
                onFocus={() => setFocus("expiry")}
                onBlur={() => setFocus("")}
              />
            </div>
          </FieldBox>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          <span style={labelStyle()}>CVC</span>
          <FieldBox focused={focus === "cvc"} style={{ padding: "0 12px 0 14px" }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <CardCvcElement
                options={{ style: ELEMENT_STYLE, placeholder: "•••" }}
                onFocus={() => setFocus("cvc")}
                onBlur={() => setFocus("")}
              />
            </div>
            <Icon name="info" size={15} style={{ color: "#52525b" }} />
          </FieldBox>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
          <span style={labelStyle(focus === "zip")}>ZIP</span>
          <FieldBox focused={focus === "zip"}>
            <input
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/[^0-9A-Za-z ]/g, "").slice(0, 10))}
              onFocus={() => setFocus("zip")}
              onBlur={() => setFocus("")}
              inputMode="numeric"
              placeholder="ZIP"
              aria-label="ZIP code"
              data-testid="checkout-zip"
              style={{
                width: "100%", background: "transparent", border: "none", outline: "none",
                color: "#fff", font: "600 16px 'Outfit'", fontVariantNumeric: "tabular-nums",
              }}
            />
          </FieldBox>
        </div>
      </div>

      {/* Pay CTA */}
      <button type="button" onClick={pay} disabled={!stripe || submitting} data-testid="checkout-pay"
        style={{
          height: 58, borderRadius: 14, background: "#00D632", color: "#0A0A0A",
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          font: "800 18px 'Manrope'", letterSpacing: "-.015em", border: "none",
          cursor: submitting ? "default" : "pointer", width: "100%", opacity: submitting ? 0.8 : 1,
        }}>
        {submitting ? (
          <><Icon name="refresh" size={16} className="animate-spin" style={{ color: "#0A0A0A" }} /> Processing…</>
        ) : (
          <><Icon name="lock" size={16} style={{ color: "#0A0A0A" }} /> Pay ${Number(amountUsd || 0).toFixed(2)}</>
        )}
      </button>
    </div>
  );
}
